import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import {
  EChecklistTaskStatus,
  ETaskId,
  OrderDetails
} from '../common/contracts';
import {
  EEventsType,
  EOrderStatus
} from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, getApiRouteOrMock } from './api.utils';

export const useSelfServe = (currentPublisherId?: string | null) => {
  const axios = useAxios();

  const checkTestPaymentCompletion = useCallback(async (updateTask: any) => {
    if (!currentPublisherId) return;

    try {
      const ordersResponse = await axios.get(
        getApiRouteOrMock(EApiRoutes.ORDERS),
        {
          offset: 0,
          recordLimit: 1,
          sortValue: 'date',
          direction: 'desc'
        },
        { 'x-publishers': currentPublisherId }
      );

      const order = ordersResponse.results.find(
        (order: OrderDetails) => order.state === EOrderStatus.charge_succeed
      );

      if (order) {
        updateTask.mutate({
          id: ETaskId.TEST_PAYMENTS,
          status: EChecklistTaskStatus.DONE
        });
        trackMixpanelEvent(EEventsType.DASHBOARD_TEST_PAYMENT_COMPLETED, {
          payment_id: order.providerPaymentId,
          amount: order.amountTotal,
          currency: order.currency,
        });
      }
    } catch (error) {
      console.error('Error checking test payment completion', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPublisherId]);

  const verifyBusiness = useMutation({
    mutationFn: async (data: {
      fullName: string;
      phoneNumber: string;
      email: string;
    }) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_VERIFY_BUSINESS),
        data,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  const sendAgreement = useMutation({
    mutationFn: async (data: {
      userId: string;
      companyId: string;
      publisherId: string;
    }) => {
      const response = await axios.post(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_SEND_AGREEMENT),
        data,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  return {
    verifyBusiness,
    sendAgreement,
    checkTestPaymentCompletion
  };
};
